import React, { useRef, useState } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import {
  Box,
  IconButton,
  TablePagination,
  TableSortLabel,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  UilPlus,
  UilInfoCircle,
  UilPen,
  UilTrashAlt,
} from "@iconscout/react-unicons";
import { useNavigate } from "react-router-dom";
import { SendDeleteRequest, SendPutRequest } from "../../actions/Actions";
import { useContext } from "react";
import AuthContext from "../../store/auth-context";
import BackdropContext from "../../store/backdrop-context";
import { containsRoles, formatDate, formatDateTime } from "../../utils/Utils";
import InvoiceStatusEnum from "../../enum/InvoiceStatusEnum";
import { baseUrl } from "../../Config";
import PropTypes from "prop-types";
import { visuallyHidden } from "@mui/utils";
import Color from "../../enum/Color";
import CreateButton from "../Common/CreateButton";
import TravelWarrantModal from "../Modal/TravelWarrantModal";
import ConfirmationModal from "../Modal/ConfirmationModal";
import { DELETE_RECORD_MESSAGE, DELETE_RECORD_TITLE } from "../../Constants/Constants";

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const headCells = [
  {
    id: "name",
    numeric: false,
    disablePadding: false,
    label: "Name",
  },
  {
    id: "owner",
    numeric: false,
    disablePadding: false,
    label: "Employee",
  },
  {
    id: "location",
    numeric: false,
    disablePadding: false,
    label: "Location",
  },
  {
    id: "departure",
    numeric: false,
    disablePadding: false,
    label: "Departure",
  },
  {
    id: "arrival",
    numeric: false,
    disablePadding: false,
    label: "Arrival",
  },
  {
    id: "distance",
    numeric: false,
    disablePadding: false,
    label: "Distance",
  },
  {
    id: "primaryActions",
    numeric: false,
    disablePadding: false,
    label: "",
  },
  {
    id: "secondaryActions",
    numeric: false,
    disablePadding: false,
    label: "",
  },
];

function EnhancedTableHead(props) {
  const { order, orderBy, rowCount, onRequestSort } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={"left"}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};

const EnhancedTableToolbar = () => {
  return (
    <Toolbar
      sx={{
        pl: { sm: 2 },
        pr: { xs: 1, sm: 1 },
      }}
    >
      <Typography
        sx={{ flex: "1 1 100%" }}
        variant="h6"
        id="tableTitle"
        component="div"
      >
        Travel Warrants
      </Typography>
    </Toolbar>
  );
};

const TravelWarrantsTable = (props) => {
  const [showModal, setShowModal] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [modalData, setModalData] = useState("");
  const [isReadOnly, setIsReadOnly] = useState(false);
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);
  const backdropContext = useContext(BackdropContext);

  const tableData = props.data;
  const vehicles = props.vehicles;
  const employee = props.employee;

  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("id");
  const [page, setPage] = useState(0);
  const [dense, setDense] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const deleteId = useRef();

  const showDetails = (rowData, isReadOnly) => {
    navigate("/travel-warrants/preview/" + rowData.id);
  };

  const editHandler = (rowData, isReadOnly) => {
    setShowModal((prev) => {
      return !prev;
    });
    setModalData(() => {
      return rowData;
    });
    setIsReadOnly(isReadOnly);
  };

  

  const deleteHandler = () => {
    const url = baseUrl + "/api/travel-warrant?id=" + deleteId.current;
    SendDeleteRequest(url, authContext.token, travelWarrantsHandler);
    backdropContext.toggle();

  };

  const travelWarrantsHandler = () => {
    backdropContext.toggle();
    props.refreshHandler();
    toggleConfirmationDialog();
  }

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const closeModalHandler = () => {
    setShowModal((prev) => {
      return !prev;
    });

    props.refreshHandler();
  };

  const newModal = () => {
    setModalData(null);
    setShowModal((prev) => {
      return !prev;
    });
  };

  const onDeleteClick = (row) => {
    deleteId.current = row.id;
    toggleConfirmationDialog();
  };
  const toggleConfirmationDialog = () => {
    setShowDialog((prev) => {
      return !prev;
    });
  };

  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - tableData.length) : 0;

  return (
    <div className="Table">
      <TravelWarrantModal
        isVisible={showModal}
        isReadOnly={isReadOnly}
        closeModalHandler={closeModalHandler}
        data={modalData}
        vehicles={vehicles}
        employee={employee}
      />

    <ConfirmationModal
        isVisible={showDialog}
        confirmAction={deleteHandler}
        denieAction={toggleConfirmationDialog}
        title={DELETE_RECORD_TITLE}
        message={DELETE_RECORD_MESSAGE}
      />
      <CreateButton action={newModal} icon={<UilPlus />} text="New Travel Warrant" />

      <Box sx={{ width: "100%" }}>
        <Paper sx={{ width: "100%", mb: 2 }} elevation={5}>
          <EnhancedTableToolbar />
          <TableContainer>
            <Table
              sx={{ minWidth: 750 }}
              aria-labelledby="tableTitle"
              size={dense ? "small" : "medium"}
            >
              <EnhancedTableHead
                order={order}
                orderBy={orderBy}
                onRequestSort={handleRequestSort}
                rowCount={tableData.length}
              />
              <TableBody>
                {/* if you don't need to support IE11, you can replace the `stableSort` call with:
                 rows.slice().sort(getComparator(order, orderBy)) */}
                {stableSort(tableData, getComparator(order, orderBy))
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row, index) => {
                    const labelId = `enhanced-table-checkbox-${index}`;

                    return (
                      <TableRow
                        hover
                        role="checkbox"
                        tabIndex={-1}
                        key={row.id}
                      >
                        <TableCell align={"left"} component="th" scope="row">
                          {row.name}
                        </TableCell>
                        <TableCell align={"left"}>
                          {row.owner}
                        </TableCell>
                        <TableCell align={"left"} component="th" scope="row">
                          {row.location}
                        </TableCell>
                        <TableCell align={"left"}>
                          {formatDateTime(row.departure)}
                        </TableCell>
                        <TableCell align={"left"}>
                          {formatDateTime(row.arrival)}
                        </TableCell>
                        <TableCell align={"left"}>
                          {row.distance + " km"}
                        </TableCell>
                        <TableCell align={"left"} className="Details">
                          <Tooltip title="Show travel warrant">
                            <IconButton onClick={() => showDetails(row, true)}>
                              <UilInfoCircle
                                style={{ color: Color.BACKGROUND }}
                              />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit travel warrant">
                            <IconButton onClick={() => editHandler(row, true)}>
                              <UilPen
                                style={{ color: Color.BACKGROUND }}
                              />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                        <TableCell align={"left"} className="Details">
                          {
                            containsRoles(
                              employee.roles.map((role) => role.name),
                              "ROLE_ADMIN"
                            ) &&
                            <Tooltip title="Delete travel warant">
                              <IconButton
                                disabled={row.status === InvoiceStatusEnum.PAID}
                                onClick={() => onDeleteClick(row)}
                              >
                                <UilTrashAlt style={{ color: Color.DANGER }}/>
                              </IconButton>
                            </Tooltip>
                          }
                        </TableCell>
                      </TableRow>
                    );
                  })}
                {emptyRows > 0 && (
                  <TableRow
                    style={{
                      height: (dense ? 33 : 53) * emptyRows,
                    }}
                  >
                    <TableCell colSpan={6} />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={tableData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      </Box>
    </div>
  );
};

export default TravelWarrantsTable;
